/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const V = globalThis, Z = V.ShadowRoot && (V.ShadyCSS === void 0 || V.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, J = Symbol(), te = /* @__PURE__ */ new WeakMap();
let _e = class {
  constructor(r, e, t) {
    if (this._$cssResult$ = !0, t !== J)
      throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = r, this.t = e;
  }
  get styleSheet() {
    let r = this.o;
    const e = this.t;
    if (Z && r === void 0) {
      const t = e !== void 0 && e.length === 1;
      t && (r = te.get(e)), r === void 0 && ((this.o = r = new CSSStyleSheet()).replaceSync(this.cssText), t && te.set(e, r));
    }
    return r;
  }
  toString() {
    return this.cssText;
  }
};
const Re = (r) => new _e(typeof r == "string" ? r : r + "", void 0, J), I = (r, ...e) => {
  const t = r.length === 1 ? r[0] : e.reduce((i, n, s) => i + ((o) => {
    if (o._$cssResult$ === !0)
      return o.cssText;
    if (typeof o == "number")
      return o;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + o + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(n) + r[s + 1], r[0]);
  return new _e(t, r, J);
}, Ue = (r, e) => {
  if (Z)
    r.adoptedStyleSheets = e.map((t) => t instanceof CSSStyleSheet ? t : t.styleSheet);
  else
    for (const t of e) {
      const i = document.createElement("style"), n = V.litNonce;
      n !== void 0 && i.setAttribute("nonce", n), i.textContent = t.cssText, r.appendChild(i);
    }
}, re = Z ? (r) => r : (r) => r instanceof CSSStyleSheet ? ((e) => {
  let t = "";
  for (const i of e.cssRules)
    t += i.cssText;
  return Re(t);
})(r) : r;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: Le, defineProperty: ke, getOwnPropertyDescriptor: Me, getOwnPropertyNames: Ie, getOwnPropertySymbols: He, getPrototypeOf: je } = Object, x = globalThis, ie = x.trustedTypes, Be = ie ? ie.emptyScript : "", ne = x.reactiveElementPolyfillSupport, U = (r, e) => r, W = { toAttribute(r, e) {
  switch (e) {
    case Boolean:
      r = r ? Be : null;
      break;
    case Object:
    case Array:
      r = r == null ? r : JSON.stringify(r);
  }
  return r;
}, fromAttribute(r, e) {
  let t = r;
  switch (e) {
    case Boolean:
      t = r !== null;
      break;
    case Number:
      t = r === null ? null : Number(r);
      break;
    case Object:
    case Array:
      try {
        t = JSON.parse(r);
      } catch {
        t = null;
      }
  }
  return t;
} }, X = (r, e) => !Le(r, e), oe = { attribute: !0, type: String, converter: W, reflect: !1, hasChanged: X };
Symbol.metadata != null || (Symbol.metadata = Symbol("metadata")), x.litPropertyMetadata != null || (x.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
class O extends HTMLElement {
  static addInitializer(e) {
    var t;
    this._$Ei(), ((t = this.l) != null ? t : this.l = []).push(e);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(e, t = oe) {
    if (t.state && (t.attribute = !1), this._$Ei(), this.elementProperties.set(e, t), !t.noAccessor) {
      const i = Symbol(), n = this.getPropertyDescriptor(e, i, t);
      n !== void 0 && ke(this.prototype, e, n);
    }
  }
  static getPropertyDescriptor(e, t, i) {
    var n;
    const { get: s, set: o } = (n = Me(this.prototype, e)) != null ? n : { get() {
      return this[t];
    }, set(a) {
      this[t] = a;
    } };
    return { get() {
      return s == null ? void 0 : s.call(this);
    }, set(a) {
      const l = s == null ? void 0 : s.call(this);
      o.call(this, a), this.requestUpdate(e, l, i);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    var t;
    return (t = this.elementProperties.get(e)) != null ? t : oe;
  }
  static _$Ei() {
    if (this.hasOwnProperty(U("elementProperties")))
      return;
    const e = je(this);
    e.finalize(), e.l !== void 0 && (this.l = [...e.l]), this.elementProperties = new Map(e.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(U("finalized")))
      return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(U("properties"))) {
      const t = this.properties, i = [...Ie(t), ...He(t)];
      for (const n of i)
        this.createProperty(n, t[n]);
    }
    const e = this[Symbol.metadata];
    if (e !== null) {
      const t = litPropertyMetadata.get(e);
      if (t !== void 0)
        for (const [i, n] of t)
          this.elementProperties.set(i, n);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [t, i] of this.elementProperties) {
      const n = this._$Eu(t, i);
      n !== void 0 && this._$Eh.set(n, t);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(e) {
    const t = [];
    if (Array.isArray(e)) {
      const i = new Set(e.flat(1 / 0).reverse());
      for (const n of i)
        t.unshift(re(n));
    } else
      e !== void 0 && t.push(re(e));
    return t;
  }
  static _$Eu(e, t) {
    const i = t.attribute;
    return i === !1 ? void 0 : typeof i == "string" ? i : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var e;
    this._$ES = new Promise((t) => this.enableUpdating = t), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (e = this.constructor.l) == null || e.forEach((t) => t(this));
  }
  addController(e) {
    var t, i;
    ((t = this._$EO) != null ? t : this._$EO = /* @__PURE__ */ new Set()).add(e), this.renderRoot !== void 0 && this.isConnected && ((i = e.hostConnected) == null || i.call(e));
  }
  removeController(e) {
    var t;
    (t = this._$EO) == null || t.delete(e);
  }
  _$E_() {
    const e = /* @__PURE__ */ new Map(), t = this.constructor.elementProperties;
    for (const i of t.keys())
      this.hasOwnProperty(i) && (e.set(i, this[i]), delete this[i]);
    e.size > 0 && (this._$Ep = e);
  }
  createRenderRoot() {
    var e;
    const t = (e = this.shadowRoot) != null ? e : this.attachShadow(this.constructor.shadowRootOptions);
    return Ue(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    var e;
    this.renderRoot != null || (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$EO) == null || e.forEach((t) => {
      var i;
      return (i = t.hostConnected) == null ? void 0 : i.call(t);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$EO) == null || e.forEach((t) => {
      var i;
      return (i = t.hostDisconnected) == null ? void 0 : i.call(t);
    });
  }
  attributeChangedCallback(e, t, i) {
    this._$AK(e, i);
  }
  _$EC(e, t) {
    var i;
    const n = this.constructor.elementProperties.get(e), s = this.constructor._$Eu(e, n);
    if (s !== void 0 && n.reflect === !0) {
      const o = (((i = n.converter) == null ? void 0 : i.toAttribute) !== void 0 ? n.converter : W).toAttribute(t, n.type);
      this._$Em = e, o == null ? this.removeAttribute(s) : this.setAttribute(s, o), this._$Em = null;
    }
  }
  _$AK(e, t) {
    var i;
    const n = this.constructor, s = n._$Eh.get(e);
    if (s !== void 0 && this._$Em !== s) {
      const o = n.getPropertyOptions(s), a = typeof o.converter == "function" ? { fromAttribute: o.converter } : ((i = o.converter) == null ? void 0 : i.fromAttribute) !== void 0 ? o.converter : W;
      this._$Em = s, this[s] = a.fromAttribute(t, o.type), this._$Em = null;
    }
  }
  requestUpdate(e, t, i) {
    var n;
    if (e !== void 0) {
      if (i != null || (i = this.constructor.getPropertyOptions(e)), !((n = i.hasChanged) != null ? n : X)(this[e], t))
        return;
      this.P(e, t, i);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$ET());
  }
  P(e, t, i) {
    var n;
    this._$AL.has(e) || this._$AL.set(e, t), i.reflect === !0 && this._$Em !== e && ((n = this._$Ej) != null ? n : this._$Ej = /* @__PURE__ */ new Set()).add(e);
  }
  async _$ET() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (t) {
      Promise.reject(t);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var e;
    if (!this.isUpdatePending)
      return;
    if (!this.hasUpdated) {
      if (this.renderRoot != null || (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [s, o] of this._$Ep)
          this[s] = o;
        this._$Ep = void 0;
      }
      const n = this.constructor.elementProperties;
      if (n.size > 0)
        for (const [s, o] of n)
          o.wrapped !== !0 || this._$AL.has(s) || this[s] === void 0 || this.P(s, this[s], o);
    }
    let t = !1;
    const i = this._$AL;
    try {
      t = this.shouldUpdate(i), t ? (this.willUpdate(i), (e = this._$EO) == null || e.forEach((n) => {
        var s;
        return (s = n.hostUpdate) == null ? void 0 : s.call(n);
      }), this.update(i)) : this._$EU();
    } catch (n) {
      throw t = !1, this._$EU(), n;
    }
    t && this._$AE(i);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var t;
    (t = this._$EO) == null || t.forEach((i) => {
      var n;
      return (n = i.hostUpdated) == null ? void 0 : n.call(i);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$EU() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$Ej && (this._$Ej = this._$Ej.forEach((t) => this._$EC(t, this[t]))), this._$EU();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
}
var se;
O.elementStyles = [], O.shadowRootOptions = { mode: "open" }, O[U("elementProperties")] = /* @__PURE__ */ new Map(), O[U("finalized")] = /* @__PURE__ */ new Map(), ne == null || ne({ ReactiveElement: O }), ((se = x.reactiveElementVersions) != null ? se : x.reactiveElementVersions = []).push("2.0.4");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ve = { attribute: !0, type: String, converter: W, reflect: !1, hasChanged: X }, Ke = (r = Ve, e, t) => {
  const { kind: i, metadata: n } = t;
  let s = globalThis.litPropertyMetadata.get(n);
  if (s === void 0 && globalThis.litPropertyMetadata.set(n, s = /* @__PURE__ */ new Map()), s.set(t.name, r), i === "accessor") {
    const { name: o } = t;
    return { set(a) {
      const l = e.get.call(this);
      e.set.call(this, a), this.requestUpdate(o, l, r);
    }, init(a) {
      return a !== void 0 && this.P(o, void 0, r), a;
    } };
  }
  if (i === "setter") {
    const { name: o } = t;
    return function(a) {
      const l = this[o];
      e.call(this, a), this.requestUpdate(o, l, r);
    };
  }
  throw Error("Unsupported decorator location: " + i);
};
function p(r) {
  return (e, t) => typeof t == "object" ? Ke(r, e, t) : ((i, n, s) => {
    const o = n.hasOwnProperty(s);
    return n.constructor.createProperty(s, o ? { ...i, wrapped: !0 } : i), o ? Object.getOwnPropertyDescriptor(n, s) : void 0;
  })(r, e, t);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function ze(r) {
  return p({ ...r, state: !0, attribute: !1 });
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const F = globalThis, Y = F.trustedTypes, ae = Y ? Y.createPolicy("lit-html", { createHTML: (r) => r }) : void 0, Q = "$lit$", g = `lit$${Math.random().toFixed(9).slice(2)}$`, ee = "?" + g, We = `<${ee}>`, S = document, L = () => S.createComment(""), k = (r) => r === null || typeof r != "object" && typeof r != "function", Ee = Array.isArray, we = (r) => Ee(r) || typeof (r == null ? void 0 : r[Symbol.iterator]) == "function", G = `[ 	
\f\r]`, R = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, le = /-->/g, ce = />/g, E = RegExp(`>|${G}(?:([^\\s"'>=/]+)(${G}*=${G}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), de = /'/g, he = /"/g, Ae = /^(?:script|style|textarea|title)$/i, Fe = (r) => (e, ...t) => ({ _$litType$: r, strings: e, values: t }), Ye = Fe(1), N = Symbol.for("lit-noChange"), u = Symbol.for("lit-nothing"), ue = /* @__PURE__ */ new WeakMap(), A = S.createTreeWalker(S, 129);
function Se(r, e) {
  if (!Array.isArray(r) || !r.hasOwnProperty("raw"))
    throw Error("invalid template strings array");
  return ae !== void 0 ? ae.createHTML(e) : e;
}
const Ce = (r, e) => {
  const t = r.length - 1, i = [];
  let n, s = e === 2 ? "<svg>" : "", o = R;
  for (let a = 0; a < t; a++) {
    const l = r[a];
    let c, d, h = -1, f = 0;
    for (; f < l.length && (o.lastIndex = f, d = o.exec(l), d !== null); )
      f = o.lastIndex, o === R ? d[1] === "!--" ? o = le : d[1] !== void 0 ? o = ce : d[2] !== void 0 ? (Ae.test(d[2]) && (n = RegExp("</" + d[2], "g")), o = E) : d[3] !== void 0 && (o = E) : o === E ? d[0] === ">" ? (o = n != null ? n : R, h = -1) : d[1] === void 0 ? h = -2 : (h = o.lastIndex - d[2].length, c = d[1], o = d[3] === void 0 ? E : d[3] === '"' ? he : de) : o === he || o === de ? o = E : o === le || o === ce ? o = R : (o = E, n = void 0);
    const y = o === E && r[a + 1].startsWith("/>") ? " " : "";
    s += o === R ? l + We : h >= 0 ? (i.push(c), l.slice(0, h) + Q + l.slice(h) + g + y) : l + g + (h === -2 ? a : y);
  }
  return [Se(r, s + (r[t] || "<?>") + (e === 2 ? "</svg>" : "")), i];
};
class M {
  constructor({ strings: e, _$litType$: t }, i) {
    let n;
    this.parts = [];
    let s = 0, o = 0;
    const a = e.length - 1, l = this.parts, [c, d] = Ce(e, t);
    if (this.el = M.createElement(c, i), A.currentNode = this.el.content, t === 2) {
      const h = this.el.content.firstChild;
      h.replaceWith(...h.childNodes);
    }
    for (; (n = A.nextNode()) !== null && l.length < a; ) {
      if (n.nodeType === 1) {
        if (n.hasAttributes())
          for (const h of n.getAttributeNames())
            if (h.endsWith(Q)) {
              const f = d[o++], y = n.getAttribute(h).split(g), j = /([.?@])?(.*)/.exec(f);
              l.push({ type: 1, index: s, name: j[2], strings: y, ctor: j[1] === "." ? Pe : j[1] === "?" ? De : j[1] === "@" ? xe : H }), n.removeAttribute(h);
            } else
              h.startsWith(g) && (l.push({ type: 6, index: s }), n.removeAttribute(h));
        if (Ae.test(n.tagName)) {
          const h = n.textContent.split(g), f = h.length - 1;
          if (f > 0) {
            n.textContent = Y ? Y.emptyScript : "";
            for (let y = 0; y < f; y++)
              n.append(h[y], L()), A.nextNode(), l.push({ type: 2, index: ++s });
            n.append(h[f], L());
          }
        }
      } else if (n.nodeType === 8)
        if (n.data === ee)
          l.push({ type: 2, index: s });
        else {
          let h = -1;
          for (; (h = n.data.indexOf(g, h + 1)) !== -1; )
            l.push({ type: 7, index: s }), h += g.length - 1;
        }
      s++;
    }
  }
  static createElement(e, t) {
    const i = S.createElement("template");
    return i.innerHTML = e, i;
  }
}
function C(r, e, t = r, i) {
  var n, s, o;
  if (e === N)
    return e;
  let a = i !== void 0 ? (n = t._$Co) == null ? void 0 : n[i] : t._$Cl;
  const l = k(e) ? void 0 : e._$litDirective$;
  return (a == null ? void 0 : a.constructor) !== l && ((s = a == null ? void 0 : a._$AO) == null || s.call(a, !1), l === void 0 ? a = void 0 : (a = new l(r), a._$AT(r, t, i)), i !== void 0 ? ((o = t._$Co) != null ? o : t._$Co = [])[i] = a : t._$Cl = a), a !== void 0 && (e = C(r, a._$AS(r, e.values), a, i)), e;
}
class Oe {
  constructor(e, t) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = t;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    var t;
    const { el: { content: i }, parts: n } = this._$AD, s = ((t = e == null ? void 0 : e.creationScope) != null ? t : S).importNode(i, !0);
    A.currentNode = s;
    let o = A.nextNode(), a = 0, l = 0, c = n[0];
    for (; c !== void 0; ) {
      if (a === c.index) {
        let d;
        c.type === 2 ? d = new T(o, o.nextSibling, this, e) : c.type === 1 ? d = new c.ctor(o, c.name, c.strings, this, e) : c.type === 6 && (d = new Ne(o, this, e)), this._$AV.push(d), c = n[++l];
      }
      a !== (c == null ? void 0 : c.index) && (o = A.nextNode(), a++);
    }
    return A.currentNode = S, s;
  }
  p(e) {
    let t = 0;
    for (const i of this._$AV)
      i !== void 0 && (i.strings !== void 0 ? (i._$AI(e, i, t), t += i.strings.length - 2) : i._$AI(e[t])), t++;
  }
}
class T {
  get _$AU() {
    var e, t;
    return (t = (e = this._$AM) == null ? void 0 : e._$AU) != null ? t : this._$Cv;
  }
  constructor(e, t, i, n) {
    var s;
    this.type = 2, this._$AH = u, this._$AN = void 0, this._$AA = e, this._$AB = t, this._$AM = i, this.options = n, this._$Cv = (s = n == null ? void 0 : n.isConnected) != null ? s : !0;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const t = this._$AM;
    return t !== void 0 && (e == null ? void 0 : e.nodeType) === 11 && (e = t.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, t = this) {
    e = C(this, e, t), k(e) ? e === u || e == null || e === "" ? (this._$AH !== u && this._$AR(), this._$AH = u) : e !== this._$AH && e !== N && this._(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : we(e) ? this.k(e) : this._(e);
  }
  S(e) {
    return this._$AA.parentNode.insertBefore(e, this._$AB);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.S(e));
  }
  _(e) {
    this._$AH !== u && k(this._$AH) ? this._$AA.nextSibling.data = e : this.T(S.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    var t;
    const { values: i, _$litType$: n } = e, s = typeof n == "number" ? this._$AC(e) : (n.el === void 0 && (n.el = M.createElement(Se(n.h, n.h[0]), this.options)), n);
    if (((t = this._$AH) == null ? void 0 : t._$AD) === s)
      this._$AH.p(i);
    else {
      const o = new Oe(s, this), a = o.u(this.options);
      o.p(i), this.T(a), this._$AH = o;
    }
  }
  _$AC(e) {
    let t = ue.get(e.strings);
    return t === void 0 && ue.set(e.strings, t = new M(e)), t;
  }
  k(e) {
    Ee(this._$AH) || (this._$AH = [], this._$AR());
    const t = this._$AH;
    let i, n = 0;
    for (const s of e)
      n === t.length ? t.push(i = new T(this.S(L()), this.S(L()), this, this.options)) : i = t[n], i._$AI(s), n++;
    n < t.length && (this._$AR(i && i._$AB.nextSibling, n), t.length = n);
  }
  _$AR(e = this._$AA.nextSibling, t) {
    var i;
    for ((i = this._$AP) == null ? void 0 : i.call(this, !1, !0, t); e && e !== this._$AB; ) {
      const n = e.nextSibling;
      e.remove(), e = n;
    }
  }
  setConnected(e) {
    var t;
    this._$AM === void 0 && (this._$Cv = e, (t = this._$AP) == null || t.call(this, e));
  }
}
class H {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, t, i, n, s) {
    this.type = 1, this._$AH = u, this._$AN = void 0, this.element = e, this.name = t, this._$AM = n, this.options = s, i.length > 2 || i[0] !== "" || i[1] !== "" ? (this._$AH = Array(i.length - 1).fill(new String()), this.strings = i) : this._$AH = u;
  }
  _$AI(e, t = this, i, n) {
    const s = this.strings;
    let o = !1;
    if (s === void 0)
      e = C(this, e, t, 0), o = !k(e) || e !== this._$AH && e !== N, o && (this._$AH = e);
    else {
      const a = e;
      let l, c;
      for (e = s[0], l = 0; l < s.length - 1; l++)
        c = C(this, a[i + l], t, l), c === N && (c = this._$AH[l]), o || (o = !k(c) || c !== this._$AH[l]), c === u ? e = u : e !== u && (e += (c != null ? c : "") + s[l + 1]), this._$AH[l] = c;
    }
    o && !n && this.j(e);
  }
  j(e) {
    e === u ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e != null ? e : "");
  }
}
class Pe extends H {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === u ? void 0 : e;
  }
}
class De extends H {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== u);
  }
}
class xe extends H {
  constructor(e, t, i, n, s) {
    super(e, t, i, n, s), this.type = 5;
  }
  _$AI(e, t = this) {
    var i;
    if ((e = (i = C(this, e, t, 0)) != null ? i : u) === N)
      return;
    const n = this._$AH, s = e === u && n !== u || e.capture !== n.capture || e.once !== n.once || e.passive !== n.passive, o = e !== u && (n === u || s);
    s && this.element.removeEventListener(this.name, this, n), o && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var t, i;
    typeof this._$AH == "function" ? this._$AH.call((i = (t = this.options) == null ? void 0 : t.host) != null ? i : this.element, e) : this._$AH.handleEvent(e);
  }
}
class Ne {
  constructor(e, t, i) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = t, this.options = i;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    C(this, e);
  }
}
const dt = { P: Q, A: g, C: ee, M: 1, L: Ce, R: Oe, D: we, V: C, I: T, H, N: De, U: xe, B: Pe, F: Ne }, pe = F.litHtmlPolyfillSupport;
var be;
pe == null || pe(M, T), ((be = F.litHtmlVersions) != null ? be : F.litHtmlVersions = []).push("3.1.3");
const Ge = (r, e, t) => {
  var i, n;
  const s = (i = t == null ? void 0 : t.renderBefore) != null ? i : e;
  let o = s._$litPart$;
  if (o === void 0) {
    const a = (n = t == null ? void 0 : t.renderBefore) != null ? n : null;
    s._$litPart$ = o = new T(e.insertBefore(L(), a), a, void 0, t != null ? t : {});
  }
  return o._$AI(r), o;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
class P extends O {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var e;
    const t = super.createRenderRoot();
    return (e = this.renderOptions).renderBefore != null || (e.renderBefore = t.firstChild), t;
  }
  update(e) {
    const t = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this._$Do = Ge(t, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this._$Do) == null || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this._$Do) == null || e.setConnected(!1);
  }
  render() {
    return N;
  }
}
var ve;
P._$litElement$ = !0, P.finalized = !0, (ve = globalThis.litElementHydrateSupport) == null || ve.call(globalThis, { LitElement: P });
const fe = globalThis.litElementPolyfillSupport;
fe == null || fe({ LitElement: P });
var me;
((me = globalThis.litElementVersions) != null ? me : globalThis.litElementVersions = []).push("4.0.5");
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Te = Symbol.for(""), qe = (r) => {
  if ((r == null ? void 0 : r.r) === Te)
    return r == null ? void 0 : r._$litStatic$;
}, Ze = (r) => ({ _$litStatic$: r, r: Te }), ge = /* @__PURE__ */ new Map(), Je = (r) => (e, ...t) => {
  const i = t.length;
  let n, s;
  const o = [], a = [];
  let l, c = 0, d = !1;
  for (; c < i; ) {
    for (l = e[c]; c < i && (s = t[c], (n = qe(s)) !== void 0); )
      l += n + e[++c], d = !0;
    c !== i && a.push(s), o.push(l), c++;
  }
  if (c === i && o.push(e[i]), d) {
    const h = o.join("$$lit$$");
    (e = ge.get(h)) === void 0 && (o.raw = o, ge.set(h, e = o)), t = a;
  }
  return r(e, ...t);
}, ht = Je(Ye);
var K = /* @__PURE__ */ ((r) => (r.NO_SYNC = "no-sync", r))(K || {}), D = /* @__PURE__ */ ((r) => (r.VISIBLE = "visible", r.IDLE = "idle", r.INTERACTION = "interaction", r.MEDIA = "media", r.DEFINED = "defined", r))(D || {}), $ = /* @__PURE__ */ ((r) => (r.BLUR = "beacon-event-blur", r.CHANGE = "beacon-event-change", r.CLICK = "beacon-event-click", r.FOCUS = "beacon-event-focus", r.HIDE = "beacon-event-hide", r.INPUT = "beacon-event-input", r.KEYDOWN = "beacon-event-keydown", r.MOUSEDOWN = "beacon-event-mousedown", r.MOUSEENTER = "beacon-event-mouseenter", r.MOUSELEAVE = "beacon-event-mouseleave", r.RENDER = "beacon-event-render", r.SORT = "beacon-event-sort", r.DEFERRED_CHILD = "beacon-event-deferred-child", r.RENDER_ACTION_ZONE = "beacon-event-render-action-zone", r.RENDER_TABLE_CARDS = "beacon-event-render-table-cards", r.LANGUAGE_SELECT = "beacon-event-language-select", r.PLAY = "beacon-event-play", r.CLOSE = "beacon-event-close", r.OPEN = "beacon-event-open", r.NAV_LANGUAGE_SELECT = "beacon-event-language-selected", r.CLOSE_ACTIVE_NAV_ITEM = "beacon-event-close-active-nav-item", r.ELEMENT_READY = "beacon-event-element-ready", r.ACTIVE_NAV_ITEM = "beacon-event-active-nav-item-change", r.DRILLDOWN = "beacon-event-drilldown", r.DRILLUP = "beacon-event-drillup", r.HEADER_ZONE_CLICK = "beacon-event-navigation-header-zone-click", r.COUNTRY_SELECTOR_CLOSE = "beacon-event-country-selector-close", r.COUNTRY_SELECTOR_OPEN = "beacon-event-country-selector-open", r.COUNTRY_FLAG_CHANGE = "beacon-event-country-flag-change", r.HAS_CURRENT_LINK = "beacon-event-has-current-link", r.REGISTER = "beacon-event-register", r.SKIP_LINK_CLICK = "beacon-event-skip-link-click", r.METRICS_SECTION_CHANGE = "beacon-event-metrics-section-change", r.METRICS_LOCATION_CHANGE = "beacon-event-metrics-location-change", r.METRICS_ID_CHANGE = "beacon-event-metrics-id-change", r.NAVIGATION_LINK_CLICK = "beacon-event-navigation-link-click", r))($ || {}), Xe = /* @__PURE__ */ ((r) => (r.CLOSE = "close", r.KEYDOWN = "keydown", r.SCROLL = "scroll", r.CLICK = "click", r.FOCUSOUT = "focusout", r))(Xe || {});
const Qe = I`[beacon-tag-name]:not([focus-override]) *:focus-visible:not(a){--heritage-width-border-normal: calc(var(--beacon-unit-base) / 8);--heritage-width-border-jumbo: calc(var(--beacon-unit-base) / 4);--heritage-width-border-maxi: calc(var(--beacon-unit-base) / 2);--total-ring-width: calc(var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) + var(--beacon-system-width-border-maxi, var(--heritage-width-border-maxi)) + var(--beacon-system-width-border-normal, var(--heritage-width-border-normal)));box-shadow:0 0 0 var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) var(--beacon-system-color-border-focus-middle, var(--beacon-color-outline-focus)),0 0 0 var(--beacon-system-width-border-maxi, var(--heritage-width-border-maxi)) var(--beacon-system-color-border-focus-outer, var(--beacon-color-shadow-focus));outline:var(--beacon-system-width-border-normal, var(--heritage-width-border-normal)) solid var(--beacon-system-color-border-focus-inner, var(--beacon-color-border-focus));outline-offset:0;position:relative;z-index:1}[beacon-tag-name]:not([focus-override]) a:focus-visible{--heritage-width-border-normal: calc(var(--beacon-unit-base) / 8);--heritage-width-border-jumbo: calc(var(--beacon-unit-base) / 4);--heritage-width-border-maxi: calc(var(--beacon-unit-base) / 2);--total-ring-width: calc(var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) + var(--beacon-system-width-border-maxi, var(--heritage-width-border-maxi)) + var(--beacon-system-width-border-normal, var(--heritage-width-border-normal)));background-color:var(--beacon-system-color-background-link-focus, var(--beacon-color-background-link-focus))!important;border-block-end-color:unset;box-shadow:0 var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) 0 0 var(--beacon-system-color-border-link-focus, var(--beacon-color-border-link-focus));color:var(--beacon-system-color-text-set-navy, var(--beacon-color-text-set-navy))!important;outline:none;text-decoration:none}@media (forced-colors: active){[beacon-tag-name]:not([focus-override]) a:focus-visible{border-block-end:var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) solid var(--beacon-system-color-border-link-focus, var(--beacon-color-border-link-focus))}}`, et = I`:host(:not([focus-override])) *:not(a):focus-visible{--heritage-width-border-normal: calc(var(--beacon-unit-base) / 8);--heritage-width-border-jumbo: calc(var(--beacon-unit-base) / 4);--heritage-width-border-maxi: calc(var(--beacon-unit-base) / 2);--total-ring-width: calc(var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) + var(--beacon-system-width-border-maxi, var(--heritage-width-border-maxi)) + var(--beacon-system-width-border-normal, var(--heritage-width-border-normal)));box-shadow:0 0 0 var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) var(--beacon-system-color-border-focus-middle, var(--beacon-color-outline-focus)),0 0 0 var(--beacon-system-width-border-maxi, var(--heritage-width-border-maxi)) var(--beacon-system-color-border-focus-outer, var(--beacon-color-shadow-focus));outline:var(--beacon-system-width-border-normal, var(--heritage-width-border-normal)) solid var(--beacon-system-color-border-focus-inner, var(--beacon-color-border-focus));outline-offset:0;position:relative;z-index:1}:host(:not([focus-override])) a:focus-visible{--heritage-width-border-normal: calc(var(--beacon-unit-base) / 8);--heritage-width-border-jumbo: calc(var(--beacon-unit-base) / 4);--heritage-width-border-maxi: calc(var(--beacon-unit-base) / 2);--total-ring-width: calc(var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) + var(--beacon-system-width-border-maxi, var(--heritage-width-border-maxi)) + var(--beacon-system-width-border-normal, var(--heritage-width-border-normal)));background-color:var(--beacon-system-color-background-link-focus, var(--beacon-color-background-link-focus))!important;border-block-end-color:unset;box-shadow:0 var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) 0 0 var(--beacon-system-color-border-link-focus, var(--beacon-color-border-link-focus));color:var(--beacon-system-color-text-set-navy, var(--beacon-color-text-set-navy))!important;outline:none;text-decoration:none}@media (forced-colors: active){:host(:not([focus-override])) a:focus-visible{border-block-end:var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) solid var(--beacon-system-color-border-link-focus, var(--beacon-color-border-link-focus))}}`, tt = I`[beacon-tag-name]:where([reset-spacing]) *{margin:0;padding:0}`, rt = I`:host{border:initial;box-sizing:border-box;display:block;font-size:initial;font-style:initial;font-variant:initial;font-weight:initial;line-height:initial;white-space:initial;word-break:break-word;word-spacing:initial;text-align:initial;text-indent:initial;text-transform:initial}:host *,:host *:before,:host *:after{box-sizing:inherit}input,button,textarea,select{font:inherit;letter-spacing:inherit}button{color:inherit;cursor:pointer}ul,ol{list-style-position:initial;list-style-type:revert}table{caption-side:initial;empty-cells:initial;border-collapse:initial;border-spacing:initial}`, it = I`.has-cell-padding{padding-block:var(--beacon-cell-space-padding);padding-inline:var(--beacon-cell-space-padding)}.has-component-padding{padding-block:var(--beacon-component-space-padding);padding-inline:var(--beacon-component-space-padding)}@media (width >= 1600px){:host{--beacon-space-default: calc(10 * var(--beacon-unit-base)) !important;--beacon-grid-layout-space-margin: 10rem !important;--beacon-grid-gutter-size-inline: 2rem !important}}:host{color:var(--beacon-system-color-text-standard, var(--beacon-color-text-default));font-family:var(--beacon-system-font-family, var(--beacon-font-family-default));-webkit-font-smoothing:antialiased}[lang=zh]{font-family:var(--beacon-system-font-family, var(--beacon-font-family-cjk))}a,::slotted(a){color:var(--beacon-system-color-text-link, var(--beacon-color-hyperlink-default))}a:not(:is(:hover,:focus)){text-decoration:none}:host(:not([motion-state=enabled])),:host(:not([motion-state=enabled])) :is(*,*:before,*:after),:host(:not([motion-state=enabled])) ::slotted(*:not([motion-state=enabled])){animation:none;animation-duration:.01ms!important;animation-iteration-count:1!important;scroll-behavior:auto!important;transition:none!important}@media (prefers-reduced-motion: reduce){:host([motion-state=enabled]),:host([motion-state=enabled]) :is(*,*:before,*:after),:host([motion-state]),:host([motion-state]) :is(*,*:before,*:after){animation:none;animation-duration:.01ms!important;animation-iteration-count:1!important;scroll-behavior:auto!important;transition:none!important}}`;
var z = /* @__PURE__ */ ((r) => (r.CLICKABLE = "data-metrics-clickable", r.TYPE = "data-metrics-type", r.TEXT = "data-metrics-link-text", r.LOCATION = "data-metrics-location", r))(z || {});
const w = class {
};
w.setPropertyOnEvent = (r, e, t, i) => {
  try {
    Object.defineProperty(r, t, {
      value: i || e[`${t}`],
      writable: !1
    });
  } catch {
  }
}, w.getAnalyticsEvents = () => {
  const r = ["gesture", "click", "touch", "change"];
  return Object.keys(window).filter((e) => /^on/.test(e) && new RegExp(r.join("|")).test(e));
}, w.setHostEventHandlerForAnalytics = (r, e) => {
  var t;
  const i = w.getAnalyticsEvents(), n = "beaconVersion";
  for (const s of i) {
    const o = s.slice(2);
    (t = r.shadowRoot) == null || t.addEventListener(
      o,
      (a) => {
        var l;
        const c = new a.constructor(o, a);
        w.setPropertyOnEvent(c, null, n, e);
        for (const d in a)
          w.setPropertyOnEvent(c, a, d);
        (l = r.shadowRoot) != null && l.contains(a.target) && r.dispatchEvent(c);
      },
      !0
    );
  }
};
let nt = w;
function pt(r, e) {
  !r || !e || r instanceof SVGElement && (r.setAttribute(z.CLICKABLE, "true"), r.setAttribute(z.TYPE, "BTN"), r.setAttribute(z.TEXT, e));
}
function B(r, e, t, i) {
  const n = new CustomEvent(e, {
    bubbles: !0,
    cancelable: !1,
    composed: !0,
    detail: {},
    ...t
  });
  return r.dispatchEvent(n), n;
}
const ye = (r) => !!r.deferrable, ot = (r) => {
  var e, t;
  return (!!((e = r.deferList) != null && e.includes(D.VISIBLE)) || !!((t = r.getAttribute("defer-until")) != null && t.includes(D.VISIBLE))) && r.isDeferUntilParent;
}, $e = (r) => !!navigator.vendor && navigator.vendor.indexOf(r) > -1;
function st(r) {
  return `${r}`.toLowerCase().replace(new RegExp(/[-_]+/, "g"), " ").replace(new RegExp(/[^\w\s]/, "g"), "").replace(new RegExp(/\s+(.)(\w*)/, "g"), (e, t, i) => `${t.toUpperCase() + i}`).replace(new RegExp(/\w/), (e) => e.toUpperCase());
}
const at = "3.3.0", lt = {
  rootMargin: "1500px",
  threshold: 0.1
}, m = {
  version: at,
  hidden: "hidden",
  safari: "Apple",
  divTagName: "div",
  deprecatedPropertyKeys: ["alternateAttributes", "sync", "alternateSlot"],
  defaultDeferOptions: lt
}, q = (r) => {
  for (const e of r)
    if (e !== void 0)
      return e;
};
var ct = Object.defineProperty, b = (r, e, t, i) => {
  for (var n = void 0, s = r.length - 1, o; s >= 0; s--)
    (o = r[s]) && (n = o(e, t, n) || n);
  return n && ct(e, t, n), n;
};
const _ = class extends P {
  constructor() {
    super(...arguments), this.isDeferUntilParent = !1, this.deferrable = !0, this.focusOverride = !1, this.resetSpacing = !1, this.beaconTagName = "", this.version = m.version, this.motionState = "disabled", this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches, this.alternateSlots = /* @__PURE__ */ new Map(), this.onslotchange = (e) => {
      var t;
      const i = (t = e.target.name) != null ? t : "";
      this.slotDeprecationWarn(i);
    }, this.resolvedDeferOptions = {
      root: document,
      rootMargin: m.defaultDeferOptions.rootMargin,
      threshold: m.defaultDeferOptions.threshold
    }, this.deferUntilVisible = () => new Promise((e) => {
      if (!("IntersectionObserver" in window))
        e();
      else {
        const t = new IntersectionObserver((i) => {
          const [n] = i;
          n.isIntersecting && (t.disconnect(), e());
        }, this.getResolvedDeferOptions() || null);
        t.observe(this);
      }
    }), this.deferUntilIdle = () => {
      const e = new Promise((t) => {
        document.readyState !== "complete" ? window.addEventListener("load", () => t(), { once: !0 }) : t();
      });
      return "requestIdleCallback" in window ? Promise.all([
        new Promise((t) => {
          requestIdleCallback(() => {
            t();
          });
        }),
        e
      ]) : e;
    }, this.deferredChildEventHandler = (e) => new Promise((t) => {
      var i;
      e instanceof CustomEvent && (i = e.detail) != null && i.type ? (this.deferOptions && this.setDeferOptions(this.deferOptions), this.selectDeferUntilHandler(e.detail.type).then(() => t())) : t();
    }), this.renderLightDOMStyles = (e = `${this.beaconTagName}-light-DOM-styles`) => {
      const t = [], i = new Set(this.constructor.lightDOMStyles.flat(1 / 0));
      for (const n of i)
        t.push(n);
      t.reverse().forEach((n, s) => {
        var o;
        const a = document.createElement("style");
        a.setAttribute("slot", "lightDOM");
        let l = !1;
        if (a.textContent = n.toString(), e) {
          const c = Math.abs(s - t.length), d = `${e}-${c}`, h = this.getClosestHost();
          a.id = d, (h && (o = h == null ? void 0 : h.shadowRoot) != null && o.querySelector(`#${d}`) || !h && document.querySelector(`#${d}`)) && (l = !0);
        }
        l || this.prepend(a);
      });
    }, this.renderComponentModeStyles = (e = `${this.beaconTagName}-mode-styles`) => {
      var t;
      (t = this.componentModeStyles) == null || t.forEach((i, n) => {
        const s = document.createElement("style");
        s.textContent = i.toString();
        const o = `${e}-${n}`;
        s.id = o, !document.querySelector(`#${o}`) && document.head.append(s);
      });
    }, this.getScopedTag = (e) => {
      if (!e)
        return null;
      const t = this.getScopedTagPrefix() ? `${this.getScopedTagPrefix()}-${e}` : e;
      return Ze(t);
    };
  }
  /**
   * Extends Lit's definition of `observedAttributes`.
   *
   * Returns a list of attributes corresponding to the registered properties.
   * @nocollapse
   * @category attributes
   */
  static get observedAttributes() {
    const e = [];
    return this.hasDeprecatedProperties && this.alternateAttributesMap.forEach((t, i) => {
      this.elementProperties.has(t) && e.push(i);
    }), e.push(...super.observedAttributes), e;
  }
  /**
   * Extends Lit's default attributeChangedCallback to support deprecated properties.
   *
   * @param name Attribute name
   * @param _old Old value
   * @param value New value
   */
  attributeChangedCallback(e, t, i) {
    const { alternateAttributesMap: n, hasDeprecatedProperties: s, elementProperties: o } = this.constructor;
    if (s && n.size > 0) {
      const a = n.get(e), l = o.get(a), c = this.getPropertyOptions(e).alternateSlot;
      if (a)
        if (a !== K.NO_SYNC && (i === null ? this.removeAttribute((l == null ? void 0 : l.attribute) || a) : this.setAttribute((l == null ? void 0 : l.attribute) || a, i || "")), c) {
          const d = this.createPropertyDeprecationForSlotMessage(e, c);
          console.warn(d);
        } else
          console.warn(this.createPropertyDeprecationMessage(e, a, l == null ? void 0 : l.attribute));
    }
    super.attributeChangedCallback(e, t, i);
  }
  /**
   * Generates a console message to log when deprecated properties were used.
   *
   * @param name attribute that triggered the message
   * @param property property that the attribute aligns to
   * @param attribute correct attribute to use
   */
  createPropertyDeprecationMessage(e, t, i) {
    let n = `Beacon Warning: [${e}] will be deprecated as a prop of <${this.beaconTagName.toLowerCase()}> in a future release.`;
    return t !== K.NO_SYNC && (n += ` Start using [${i || t}] instead to avoid future disruptions.`), n;
  }
  /**
   * Generates a console message to log when a deprecated property is used
   * and should instead use the designated slot
   * @param property the name of the property being deprecated
   * @param slot the designated slot to use instead
   */
  createPropertyDeprecationForSlotMessage(e, t) {
    return `Beacon Warning: '${e}' will be deprecated as a prop of <${this.beaconTagName.toLowerCase()}> in a future release. Start using the slot: '${t}' instead to avoid future disruptions`;
  }
  /**
   * Extend's Lit's `createProperty` method for our own uses.
   * @param name Property Name
   * @param options Options for the property
   */
  static createProperty(e, t) {
    if (t) {
      const { alternateAttributes: i, attribute: n } = t, s = Object.prototype.hasOwnProperty.call(t, "sync");
      if (m.deprecatedPropertyKeys.forEach((o) => {
        Object.prototype.hasOwnProperty.call(t, o) && (this.hasDeprecatedProperties = !0);
      }), i && i.forEach((o) => {
        this.alternateAttributesMap.set(o, e);
      }), s && !t.sync) {
        const o = n != null ? n : e;
        this.alternateAttributesMap.set(o, K.NO_SYNC);
      }
    }
    super.createProperty(e, t);
  }
  /**
   * This is used to return the property options of a property when given only its attribute name,
   * e.g. the property pageTitle with an attribute name of 'page-title' will have no way of accessing its options
   * directly when attempting to grab the options with 'page-title' and elementProperties.get().
   * @param name the resolved attribute name of a `@property()` decorated property
   * @returns the options passed in `@property()` e.g. `{attribute: 'attribute-name', reflect: true}`
   */
  getPropertyOptions(e) {
    var t;
    const { elementProperties: i } = this.constructor;
    return (t = i.get(e)) != null ? t : [...i.values()].find((n) => n.attribute === e);
  }
  /**
   * Getter that returns private resolvedDeferOptions
   * @returns resolvedDeferOptions that are passed to an IntersectionObserver
   */
  getResolvedDeferOptions() {
    return this.resolvedDeferOptions;
  }
  /**
   * Converts space separated list prop of `defer-until` to a DeferUntil enum array
   */
  get deferList() {
    var e, t;
    return (t = (e = this.deferUntil) == null ? void 0 : e.split(" ").map((i) => i).filter((i) => !!i)) != null ? t : [];
  }
  /**
   * Logs a warning based on the slot name provided and details from the alternate slots map
   *
   * @param slotName name of the slot that has changed
   */
  slotDeprecationWarn(e) {
    const t = this.alternateSlots.get(e);
    if (t !== void 0) {
      let i;
      const n = `The '${e}' for for '${this.beaconTagName}'`;
      Object.keys(t).some((s) => s.match("alternate")) ? t.alternateAttribute ? i = `will be replaced by the element property '${t.alternateAttribute}'` : i = `will be replaced by the '${t.alternateSlots}' slot` : i = "will be removed", console.warn(`Beacon Deprecation Warning: ${n} ${i} in foundation@${t.version}`);
    }
  }
  /**
   * A setter that takes user input deferOptions and resolves them compared to existing deferOptions. Enables the setting
   * of isolated deferOptions and leaves unchanged options previously set intact.
   * @param options the defer-options object used in IntersectionObserver.
   */
  setDeferOptions(e) {
    if (!e)
      return;
    const t = { ...this.resolvedDeferOptions };
    this.resolvedDeferOptions = {
      root: q([e == null ? void 0 : e.root, t == null ? void 0 : t.root, document]),
      rootMargin: q([
        e == null ? void 0 : e.rootMargin,
        t == null ? void 0 : t.rootMargin,
        m.defaultDeferOptions.rootMargin
      ]),
      threshold: q([
        e == null ? void 0 : e.threshold,
        t == null ? void 0 : t.threshold,
        m.defaultDeferOptions.threshold
      ])
    };
  }
  /**
   * Hides the visibility of the component. Used when component is deferred until specified condition.
   * @returns void
   */
  setDeferVisibilityStyles() {
    $e(m.safari) || (this.style.visibility = m.hidden);
  }
  /**
   * Sets the component styles to visibile. Used when component meets condition specified to defer until.
   * @returns void
   */
  removeDeferVisibilityStyles() {
    $e(m.safari) || this.style.removeProperty("visibility");
  }
  /**
   * Searches for a parent of component with defer-until attribute that is not solely defer-until="defined".
   * @returns closest lightDOM parent with [defer-until] attribute or null
   */
  getClosestDeferredParent() {
    const e = '[defer-until]:not([defer-until="defined"])', t = this.closest(e);
    if (t && ye(t))
      return t;
    const i = this.getClosestHost();
    if (i) {
      const n = i.closest(e);
      if (n && ye(n))
        return n;
    }
    return null;
  }
  /**
   * Runs in shadowDOM context. This method searches for the ShadowRoot then returns host of ShadowRoot.
   * @returns host element of this component of the shadowDOM
   */
  getClosestHost() {
    let e = this && this.parentNode;
    for (; e; ) {
      if (e instanceof ShadowRoot)
        return e.host;
      e = e.parentNode;
    }
    return null;
  }
  /**
   * Selects which deferUntil handler method to run on component.
   * @param deferUntilCriteria DeferUntil option that determines how component should defer.
   */
  selectDeferUntilHandler(e) {
    return new Promise((t) => {
      e.includes(D.VISIBLE) ? (this.setDeferVisibilityStyles(), this.deferUntilVisible().then(() => {
        this.removeDeferVisibilityStyles(), t();
      })) : e.includes(D.IDLE) ? (this.setDeferVisibilityStyles(), this.deferUntilIdle().then(() => {
        this.removeDeferVisibilityStyles(), t();
      })) : t();
    });
  }
  /**
   * Method that handles setup of deferUntil handler when deferUntil is configured locally via deferUntil prop on the component.
   */
  handleLocalDeferUntil() {
    return new Promise((e) => {
      this.deferOptions && this.setDeferOptions(this.deferOptions), this.selectDeferUntilHandler(this.deferList).then(() => e());
    });
  }
  /**
   * Method that looks for the closest element to the component with 'defer-until' attribute and either
   * applies the parent's deferUntil logic to the component immediately, if the parent is already registered as `isDeferUntilParent`,
   * or adds an eventListener to toggle parent's deferUntil logic on component once parent has emitted the beacon-event-deferred-child Event.
   * @param closestDeferredParent is the closest element to the component that has the 'defer-until' attribute set.
   */
  handleParentalDeferUntil(e) {
    return new Promise((t) => {
      if (e === null)
        t();
      else if (ot(e))
        this.selectDeferUntilHandler(e.deferList).then(() => t());
      else {
        const i = (n) => {
          this.deferredChildEventHandler(n).then(() => {
            e.removeEventListener($.DEFERRED_CHILD, i), t();
          });
        };
        e.addEventListener($.DEFERRED_CHILD, i);
      }
    });
  }
  /**
   * This method handles defer logic. If component has deferUntil prop set, it calls method to handle deferUntil logic locally.
   * Else if component has a closest element with 'defer-until' attribute set, it handles deferUntil based on closestDeferredParent's deferUntil settings.
   * If neither of the above criteria match, it completes connectedCallback.
   */
  handleDefer() {
    return new Promise((e) => {
      if (!this.deferrable || this.motionState === "enabled")
        e();
      else if (this.deferUntil)
        this.registerAsDeferUntilParent(), this.handleLocalDeferUntil().then(() => e());
      else {
        const t = this.getClosestDeferredParent();
        t ? this.handleParentalDeferUntil(t).then(() => e()) : e();
      }
    });
  }
  /**
   * Method that registers a component as parental component that can trigger deferUntil logic in child components. It should be placed in connectedCallback
   * of all components that need to trigger deferUntil logic in child components.
   */
  registerAsDeferUntilParent() {
    if (this.deferOptions && this.setDeferOptions(this.deferOptions), this.deferList.includes(D.VISIBLE)) {
      const e = new IntersectionObserver((t) => {
        for (const i of t)
          i.target && (this.isDeferUntilParent = !0, this.emitDeferredChild(), e.disconnect());
      }, this.getResolvedDeferOptions() || null);
      e.observe(this);
    }
  }
  /**
   * @returns the scope prefix from the beginning of the tagName.
   */
  getScopedTagPrefix() {
    const e = this.tagName.toLowerCase().split("-beacon")[0];
    return e.startsWith("beacon") ? "" : e;
  }
  /**
   * Defines this web component in the CustomElementRegistry.
   * @param tagNamePrefix lowercase string that prefixes default component tag names for scoping purposes
   */
  static registerComponent(e) {
    if (this.tagName) {
      const t = `${e ? `${e}-` : ""}${this.tagName}`;
      if (!customElements.get(t)) {
        const i = class extends this {
        };
        Object.defineProperty(i, "name", { value: st(t) }), customElements.define(t, i), customElements.whenDefined(t).then(() => {
          const n = new CustomEvent($.REGISTER, { detail: t });
          document.dispatchEvent(n);
        }).catch((n) => {
          console.error(`Error defining Beacon Component ${t} in CustomElementsRegistry: `, n);
        });
      }
    }
  }
  /**
   * This will return beacon tag name to scoped tag Name
   * @param tag beaconTagName
   * @returns Scoped tagName
   */
  getScopedTagName(e) {
    return e ? this.getScopedTagPrefix() ? `${this.getScopedTagPrefix()}-${e}` : e : "";
  }
  /**
   * @param event
   * @event {BeaconEvents} beacon-event-click - emits when clicked
   */
  emitClick(e) {
    B(this, $.CLICK, !!this.onBeaconClick && this.onBeaconClick(e));
  }
  /**
   * @param event
   * @event {BeaconEvents} beacon-event-keydown - emits when a keydown event would be fired
   */
  emitKeydown(e, t) {
    B(this, $.KEYDOWN, !!this.onBeaconKeydown && this.onBeaconKeydown(t));
  }
  /**
   * Emitter of beacon-event-deferred-child event used by components registered as deferUntilParents.
   * @param event
   * @event {BeaconEvents} beacon-event-deferred-child - signals to children of a deferUntil parent that they should behave as deferUntil components
   */
  emitDeferredChild(e) {
    B(this, $.DEFERRED_CHILD, {
      detail: {
        element: this,
        type: this.deferList
      }
    });
  }
  /**
   * Emits a beacon-event-keydown event as a default to mousedown handling
   * @param event
   */
  handleKeydown(e) {
    this.emitKeydown(e);
  }
  /**
   * Check if the component has a specific mixin applied.
   * @param name Name of the mixin to look for.
   * @returns True if the class contains that mixin.
   */
  hasMixin(e) {
    var t;
    try {
      if (!((t = this.constructor.mixins) != null && t.includes(e)))
        throw new Error(`This class does not implement ${e}.`);
      return !0;
    } catch (i) {
      return console.error(i), !1;
    }
  }
  /**
   * Check for a field with a specific value on the class.
   *
   * Useful when a mixin relies on the existance of another mixin.
   *
   * @param field Class property to check for on the mixin.
   * @param value Value of the given class field to check for.
   * @returns True if the value of the field is the same.
   */
  hasFieldAndValue(e, t) {
    try {
      if (!(e in this))
        throw new Error(`${e} does not exist on this: ${this}`);
      return this[e] === t;
    } catch (i) {
      return console.error(i), !1;
    }
  }
  /**
   * Placeholder method that collects all method functionality that is decorated with @prerender
   * and is called in connectedCallback. This method should not be overridden directly.
   * The goal is to prevent direct overrides of connectedCallback in subclasses
   */
  prerender() {
  }
  /**
   * Placeholder method that collects all method functionality that is decorated with @postrender
   * and is called in firstUpdated. This method should not be overridden directly.
   * The goal is to prevent direct overrides of firstUpdated in subclasses
   */
  postrender() {
  }
  /**
   * Placeholder method that collects all method functionality that is decorated with @destroy
   * and is called in disconnectedCallback. This method should not be overridden directly.
   * The goal is to prevent direct overrides of disconnectedCallback in subclasses
   */
  destroy() {
  }
  /**
   * Enables or disables motion on this component.
   *
   * Looks for the closest element with `[motion-state]` to find its value and set the motion state of the component equal to that.
   *
   * @link https://developer.mozilla.org/en-US/docs/Web/API/Element/closest Element.closest() will return the element itself if it matches the selector.
   */
  setMotionState() {
    if (this.motionState === "enabled")
      return;
    const e = this.closest("[motion-state]"), t = e == null ? void 0 : e.getAttribute("motion-state");
    t && (t === "enabled" || t === "disabled") && (this.motionState = t);
  }
  connectedCallback() {
    this.setMotionState(), this.handleDefer().then(() => {
      super.connectedCallback(), nt.setHostEventHandlerForAnalytics(this, this.version), this.renderLightDOMStyles(), this.prerender();
    });
  }
  firstUpdated() {
    this.postrender(), B(this, $.ELEMENT_READY);
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this.destroy();
  }
};
_.styles = [rt, it, et], _.tagName = "", _.shadowRootOptions = { ...P.shadowRootOptions, delegatesFocus: !0 }, _.alternateAttributesMap = /* @__PURE__ */ new Map(), _.hasDeprecatedProperties = !1, _.mixins = [], _.lightDOMStyles = [tt, Qe];
let v = _;
b([
  p({ attribute: "focus-override", type: Boolean, reflect: !0 })
], v.prototype, "focusOverride");
b([
  p({ attribute: "reset-spacing", type: Boolean, reflect: !0 })
], v.prototype, "resetSpacing");
b([
  p({ attribute: "beacon-tag-name", type: String, reflect: !0 })
], v.prototype, "beaconTagName");
b([
  p({ attribute: "beacon-version", reflect: !0 })
], v.prototype, "version");
b([
  p({ attribute: "defer-until" })
], v.prototype, "deferUntil");
b([
  p({ attribute: "defer-options" })
], v.prototype, "deferOptions");
b([
  p({ type: Function })
], v.prototype, "onBeaconClick");
b([
  p({ type: Function })
], v.prototype, "onBeaconKeydown");
b([
  p({ attribute: "motion-state", reflect: !0 })
], v.prototype, "motionState");
b([
  ze()
], v.prototype, "prefersReducedMotion");
export {
  Ze as Be,
  I as H,
  Ge as Je,
  N as T,
  z as W,
  ze as Ye,
  Ye as Ze,
  $ as _,
  p as b,
  ht as bt,
  pt as ft,
  v as p,
  dt as pt,
  Xe as tt,
  u,
  B as z
};
